/**
 * grade-multi.js — multi-card scan & selection UI for the grading page.
 *
 * Flow: "Multiple cards in one photo?" → /api/detect-multi (free scan:
 * detect + sub-crops + thumbs) → selection panel (tiles, type badges,
 * optional identify pass) → /api/grade-batch (per-card detached GradeJobs,
 * full v3 pipeline) → /dashboard/batch/<id> hybrid page.
 *
 * Credit wall: grade-batch 402s carry a resume_token — threaded through
 * CreditUpsell → checkout → ?paid=1&resume=<tok> → panel rehydrates with
 * the saved selection and one explicit "resume" click finishes the order.
 * ?scan=<id> deep-links hydrate an existing scan (crop→grade upsell path).
 */
(function () {
    'use strict';

    var form = document.querySelector('#grading-form form.upload-form');
    if (!form) return;

    var isBatchPage = location.pathname === '/services/batch-grading';
    var csrf = (window.csrfToken || (form.querySelector('input[name="csrf_token"]') || {}).value || '');
    var pricePer = null;
    var scanId = null;
    var scanCards = [];
    var resumeToken = null;
    var panel = null;
    var fileUrls = []; // multi-file mode: staged /api/batch-upload urls, index-aligned

    // Screen Wake Lock — iOS Safari suspends a backgrounded tab's network
    // mid-upload (server sees UPLOAD_ERR_PARTIAL). Held across the upload
    // window; iOS 16.4+, silent no-op elsewhere.
    var wakeLock = {
        _s: null,
        acquire: function () {
            try {
                if ('wakeLock' in navigator && !this._s) {
                    var self = this;
                    navigator.wakeLock.request('screen').then(function (s) {
                        self._s = s;
                        s.addEventListener('release', function () { self._s = null; });
                    }).catch(function () {});
                }
            } catch (e) {}
        },
        release: function () { try { if (this._s) { var s = this._s; this._s = null; s.release(); } } catch (e) {} }
    };
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible' && window.gradingInProgress) wakeLock.acquire();
    });

    // Full reset of the armed scan/file state — must run when a new pick
    // starts AND on dismiss, or a stale resume_token/fileUrls set rides the
    // next submitBatch POST and silently grades the OLD selection.
    function clearScanState() {
        scanId = null;
        scanCards = [];
        fileUrls = [];
        resumeToken = null;
        pricePer = null;
    }

    function esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    function fmtCredits(n) {
        return (Math.round(n * 10) / 10) + ' credit' + (n === 1 ? '' : 's');
    }

    // ── Busy lock: while a scan/submit is in flight, freeze every control in
    // the grading card (main submit, panel buttons, checkboxes, links) so
    // nothing can double-submit, and surface a status line + spinner.
    var busyEls = [];

    (function () {
        var st = document.createElement('style');
        st.textContent = '@keyframes pgcMultiSpin{to{transform:rotate(360deg)}}';
        document.head.appendChild(st);
    })();

    function lockUI(on) {
        var scope = document.getElementById('grading-form');
        if (!scope) return;
        if (on) {
            busyEls = [];
            scope.querySelectorAll('button, input, select, a[href]').forEach(function (el) {
                if (el.disabled || el.type === 'hidden') return;
                busyEls.push(el);
                el.disabled = true;
                el.style.pointerEvents = 'none';
                el.style.opacity = '0.6';
            });
        } else {
            busyEls.forEach(function (el) {
                el.disabled = false;
                el.style.pointerEvents = '';
                el.style.opacity = '';
            });
            busyEls = [];
        }
    }

    var SPINNER = '<span style="display:inline-block;width:14px;height:14px;flex-shrink:0;border:2px solid #bfdbfe;border-top-color:#1d4ed8;border-radius:50%;animation:pgcMultiSpin .8s linear infinite;"></span>';

    function showStatus(msg) {
        if (!panel) return;
        var s = panel.querySelector('[data-role=status]');
        if (!s) return;
        s.innerHTML = SPINNER + '<span>' + esc(msg) + '</span>';
        s.style.display = 'flex';
    }

    function hideStatus() {
        var s = panel && panel.querySelector('[data-role=status]');
        if (s) { s.style.display = 'none'; s.innerHTML = ''; }
    }

    // Terminal state for the scan/upload phase: releases the lock, drops any
    // armed scan/file state, and renders the message WITH a dismiss control —
    // without one, a dead panel keeps #multi-sel-panel alive forever and the
    // single-card auto-submit guard in upload-handler.js never fires again.
    function failScan(msg, info) {
        lockUI(false);
        window.gradingInProgress = false;
        clearScanState();
        if (!panel) buildPanel();
        var p = panel;
        p.innerHTML = '<div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;padding:4px 0;">'
            + '<div style="color:' + (info ? '#475569' : '#b91c1c') + ';font-size:13px;line-height:1.5;white-space:pre-line;">' + esc(msg) + '</div>'
            + '<button type="button" data-role="fail-dismiss" aria-label="Dismiss" '
            + 'style="flex:none;background:none;border:none;font-size:18px;color:#64748b;cursor:pointer;padding:0 2px;line-height:1;">×</button></div>';
        p.querySelector('[data-role=fail-dismiss]').addEventListener('click', function () {
            p.remove();
            if (panel === p) panel = null;
        });
        try { p.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
    }

    // ── Panel markup ──
    function buildPanel() {
        if (panel) return panel;
        panel = document.createElement('div');
        panel.id = 'multi-sel-panel';
        panel.style.cssText = 'margin-top:16px;background:#fff;border:1px solid #e2e8f0;border-radius:14px;padding:16px;';
        form.parentNode.insertBefore(panel, form.nextSibling);
        return panel;
    }

    function selectedIdxs() {
        var out = [];
        panel.querySelectorAll('input[type=checkbox][data-idx]:checked').forEach(function (cb) {
            out.push(parseInt(cb.getAttribute('data-idx'), 10));
        });
        return out;
    }

    function refreshFooter() {
        var sel = selectedIdxs();
        var total = pricePer ? sel.length * pricePer : null;
        var btn = panel.querySelector('[data-role=submit]');
        var lbl = panel.querySelector('[data-role=cost]');
        if (lbl) lbl.textContent = total !== null
            ? sel.length + ' selected · ' + fmtCredits(total)
            : sel.length + ' selected';
        if (btn) {
            btn.disabled = sel.length === 0;
            btn.textContent = sel.length
                ? '⚡ Grade ' + sel.length + ' card' + (sel.length > 1 ? 's' : '') + (total !== null ? ' — ' + fmtCredits(total) : '')
                : 'Select cards to grade';
        }
    }

    function renderCards(cards) {
        var grid = panel.querySelector('[data-role=grid]');
        if (!grid) return;
        grid.innerHTML = '';
        cards.forEach(function (c) {
            var tile = document.createElement('label');
            tile.style.cssText = 'position:relative;display:block;border:2px solid #e2e8f0;border-radius:12px;overflow:hidden;cursor:pointer;background:#f8fafc;transition:border-color .15s;';
            tile.innerHTML =
                '<input type="checkbox" data-idx="' + c.idx + '" checked style="position:absolute;top:8px;left:8px;width:18px;height:18px;accent-color:#22c55e;z-index:2;">' +
                '<div style="aspect-ratio:5/7;background:#e2e8f0;display:flex;align-items:center;justify-content:center;overflow:hidden;">' +
                (c.thumb_url || c.sub_url
                    ? '<img src="' + esc(c.thumb_url || c.sub_url) + '" alt="Card ' + (c.idx + 1) + '" style="width:100%;height:100%;object-fit:cover;" loading="lazy">'
                    : '<span style="font-size:28px;">🃏</span>') +
                '</div>' +
                '<div style="padding:8px 10px;font-size:11px;line-height:1.35;">' +
                '<div data-role="cname" style="font-weight:700;color:#0f172a;">' + esc(c.id_name || c.type || ('Card ' + (c.idx + 1))) + '</div>' +
                '<div style="color:#64748b;">' + (c.prob ? Math.round(c.prob * 100) + '% match' : '') + '</div>' +
                '<div data-role="cval" style="color:#059669;font-weight:700;">' + (c.est_value != null ? '≈ $' + esc(c.est_value) : '') + '</div>' +
                '</div>';
            var cb = tile.querySelector('input');
            cb.addEventListener('change', function () {
                tile.style.borderColor = cb.checked ? '#22c55e' : '#e2e8f0';
                refreshFooter();
            });
            tile.style.borderColor = '#22c55e';
            grid.appendChild(tile);
        });
        refreshFooter();
    }

    function renderPanel(cards, opts) {
        opts = opts || {};
        buildPanel();
        panel.innerHTML =
            '<div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-bottom:12px;">' +
            '  <div><div style="font-size:15px;font-weight:800;color:#0f172a;">' + cards.length + ' cards detected' + (opts.restored ? ' — restored after purchase' : '') + '</div>' +
            '  <div style="font-size:12px;color:#64748b;">Pick the cards to grade. Each runs the full grading pipeline with pricing.</div></div>' +
            '  <div style="display:flex;gap:8px;flex-wrap:wrap;">' +
            '    <button type="button" data-role="identify" class="btn btn-secondary" style="font-size:11px;padding:6px 10px;border-radius:8px;">🔎 Identify cards &amp; values</button>' +
            '    <button type="button" data-role="dismiss" class="btn btn-secondary" style="font-size:11px;padding:6px 10px;border-radius:8px;">✕ Single card instead</button>' +
            '  </div>' +
            '</div>' +
            '<div data-role="grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:10px;"></div>' +
            '<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-top:14px;">' +
            '  <div data-role="cost" style="font-size:13px;font-weight:700;color:#0f172a;"></div>' +
            '  <button type="button" data-role="submit" class="btn btn-primary" style="background:#22c55e;border:none;font-weight:700;font-size:14px;padding:10px 20px;border-radius:10px;"></button>' +
            '</div>' +
            '<div data-role="status" style="display:none;align-items:center;gap:8px;margin-top:10px;padding:10px 12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:10px;font-size:13px;color:#1e40af;font-weight:600;"></div>' +
            '<div data-role="notice" style="margin-top:8px;font-size:12px;color:#b91c1c;display:none;"></div>';

        renderCards(cards);
        if (opts.selected && opts.selected.length) {
            panel.querySelectorAll('input[type=checkbox][data-idx]').forEach(function (cb) {
                var on = opts.selected.indexOf(parseInt(cb.getAttribute('data-idx'), 10)) !== -1;
                cb.checked = on;
                cb.closest('label').style.borderColor = on ? '#22c55e' : '#e2e8f0';
            });
            refreshFooter();
        }

        panel.querySelector('[data-role=dismiss]').addEventListener('click', function () {
            panel.remove(); panel = null;
            clearScanState();
        });
        if (!scanId) {
            panel.querySelector('[data-role=identify]').style.display = 'none';
        } else {
            panel.querySelector('[data-role=identify]').addEventListener('click', identifyCards);
        }
        panel.querySelector('[data-role=submit]').addEventListener('click', submitBatch);
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function notice(msg) {
        var n = panel && panel.querySelector('[data-role=notice]');
        if (n) { n.textContent = msg; n.style.display = 'block'; }
    }

    function updateCardMeta(cards) {
        cards.forEach(function (c) {
            scanCards[c.idx] = Object.assign(scanCards[c.idx] || {}, c);
            var tiles = panel.querySelectorAll('input[data-idx="' + c.idx + '"]');
            if (!tiles.length) return;
            var tile = tiles[0].closest('label');
            var nm = tile.querySelector('[data-role=cname]');
            var val = tile.querySelector('[data-role=cval]');
            if (nm && c.id_name) nm.textContent = c.id_name;
            if (val && c.est_value != null) val.textContent = '≈ $' + c.est_value;
        });
    }

    // ── Identify pass (one analyze_all call on the stored scan — no re-upload) ──
    function identifyCards() {
        if (!scanId) return;
        var btn = panel.querySelector('[data-role=identify]');
        btn.disabled = true; btn.textContent = 'Identifying…';
        var fd = new FormData();
        fd.append('csrf_token', csrf);
        fd.append('scan_id', scanId);
        fd.append('identify', '1');
        fetch('/api/detect-multi', { method: 'POST', body: fd, credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (j) {
                btn.disabled = false;
                if (j.success && j.cards) {
                    btn.textContent = '✓ Identified';
                    updateCardMeta(j.cards);
                } else {
                    btn.textContent = '🔎 Identify cards & values';
                    notice(j.error || 'Identification failed — try again.');
                }
            })
            .catch(function () { btn.disabled = false; btn.textContent = '🔎 Identify cards & values'; });
    }

    // ── Submit → detached per-card grade jobs ──
    function submitBatch() {
        var sel = selectedIdxs();
        if (!sel.length) return;
        // File mode has no scanId — it submits image_urls_json instead; the
        // old `!scanId` guard made "Grade N cards" a silent no-op there.
        if (!scanId && !fileUrls.length && !resumeToken) return;
        var btn = panel.querySelector('[data-role=submit]');
        btn.disabled = true;
        btn.textContent = 'Starting ' + sel.length + ' grade job' + (sel.length > 1 ? 's' : '') + '…';
        lockUI(true);
        window.gradingInProgress = true;
        showStatus('Queuing ' + sel.length + ' card' + (sel.length > 1 ? 's' : '') + ' — you\'ll be redirected to the batch dashboard when it\'s ready. Do not close this page.');
        var fd = new FormData();
        fd.append('csrf_token', csrf);
        if (resumeToken) {
            fd.append('resume_token', resumeToken);
        } else {
            if (scanId) fd.append('scan_id', scanId);
            else if (fileUrls.length) fd.append('image_urls_json', JSON.stringify(fileUrls));
            fd.append('selected_json', JSON.stringify(sel));
        }
        fd.append('include_pricing', '1');
        fetch('/api/grade-batch', { method: 'POST', body: fd, credentials: 'same-origin' })
            .then(function (r) {
                return r.json().then(function (j) { return { status: r.status, j: j }; });
            })
            .then(function (res) {
                var j = res.j;
                if (res.status === 402 || (j && j.credits_required)) {
                    lockUI(false); hideStatus();
                    window.gradingInProgress = false;
                    btn.disabled = false; refreshFooter();
                    if (window.CreditUpsell) {
                        CreditUpsell.show(j.credits_required || sel.length * pricePer, j.credits_available || 0, { resumeToken: j.resume_token || null });
                    } else {
                        notice(j.error || 'Insufficient credits.');
                        try { panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
                    }
                    return;
                }
                if (j && j.success && j.batch_url) {
                    if (window.PgcStash) PgcStash.clear('grade_multi');
                    window.gradingInProgress = false;
                    location.href = j.batch_url;
                    return;
                }
                lockUI(false); hideStatus();
                window.gradingInProgress = false;
                btn.disabled = false; refreshFooter();
                notice((j && j.error) || 'Could not start the batch — try again.');
                try { panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
            })
            .catch(function () {
                lockUI(false); hideStatus();
                window.gradingInProgress = false;
                btn.disabled = false; refreshFooter();
                notice('Network error — try again.');
                try { panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
            });
    }

    // ── File prep: this picker bypasses upload-handler's handleFile(), so
    // picked files arrive raw — multi-card shots are usually full-res and can
    // blow past the server's 16MB cap (detect-multi rejects with a dead-end).
    // Reuse upload-handler's globals (loaded earlier in the same deferred
    // footer block) for HEIC convert + auto-orient + downscale. Raw-HEIC that
    // the browser can't decode passes through untouched — the server
    // transcodes/pre-scales it via Imagick.
    var SCAN_MAX = 16 * 1024 * 1024;

    function prepScanFile(file) {
        return Promise.resolve().then(function () {
            var isHeic = typeof isHeicFile === 'function' && isHeicFile(file);
            var rawHeic = false;
            var step;
            if (isHeic && typeof convertHeicToJpeg === 'function') {
                step = convertHeicToJpeg(file).catch(function (e) {
                    rawHeic = true;
                    file._heicRawFallback = true;
                    if (typeof logUploadError === 'function') logUploadError('multi_scan_heic', e && e.message || 'heic2any failed', file);
                    return file;
                });
            } else {
                step = Promise.resolve(file);
            }
            return step.then(function (f) {
                if (!rawHeic && typeof autoOrientImage === 'function') {
                    return autoOrientImage(f).catch(function () { return f; });
                }
                return f;
            }).then(function (f) {
                if (!rawHeic && f.size > SCAN_MAX && typeof downscaleImageIfNeeded === 'function') {
                    return downscaleImageIfNeeded(f, SCAN_MAX).catch(function () { return f; });
                }
                return f;
            }).then(function (f) {
                // Still over the cap after client downscale (or undecodable
                // in-browser) — don't dead-end: the server pre-scales
                // oversized uploads via Imagick. Log for visibility only.
                if (f.size > SCAN_MAX && typeof logUploadError === 'function') {
                    logUploadError('multi_scan_size', 'exceeds 16MB after client prep — sent for server prescale', f);
                }
                return f;
            });
        });
    }

    // ── Scan upload → detect-multi ──
    function runScan(file) {
        clearScanState();
        var p = buildPanel();
        p.innerHTML = '<div style="padding:30px;text-align:center;color:#64748b;font-size:13px;display:flex;align-items:center;justify-content:center;gap:8px;" data-role="scan-status">' + SPINNER + '<span>Scanning your photo for cards — this can take up to a minute…</span></div>';
        lockUI(true);
        window.gradingInProgress = true;
        try { p.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
        var stEl = p.querySelector('[data-role=scan-status] span:last-child');
        var slowTimer = setTimeout(function () {
            if (stEl) stEl.textContent = 'Still scanning — detecting and cropping each card. Do not close this page.';
        }, 15000);
        wakeLock.acquire();
        var done = function () { clearTimeout(slowTimer); wakeLock.release(); };
        prepScanFile(file).then(function (prepped) {
        var fd = new FormData();
        fd.append('csrf_token', csrf);
        fd.append('image', prepped);
        fd.append('mode', 'grade');
        fetch('/api/detect-multi', { method: 'POST', body: fd, credentials: 'same-origin' })
            .then(function (r) { return r.json().then(function (j) { return { status: r.status, j: j }; }); })
            .then(function (res) {
                done();
                var j = res.j;
                if (j && j.redirect) { window.gradingInProgress = false; location.href = j.redirect; return; }
                if (!j || !j.success) {
                    failScan((j && j.error) || 'Scan failed — try again.');
                    return;
                }
                if (j.count < 2) {
                    failScan('Only ' + (j.count || 'no') + ' card' + (j.count === 1 ? '' : 's') + ' detected — use the normal upload above for a single card.', true);
                    return;
                }
                lockUI(false);
                window.gradingInProgress = false;
                scanId = j.scan_id;
                scanCards = j.cards;
                pricePer = j.price && j.price.grade;
                renderPanel(j.cards);
            })
            .catch(function () {
                done();
                failScan('Network error — the scan could not finish. Check your connection and try again.');
            });
        }).catch(function (e) {
            done();
            failScan((e && e.userMsg) || 'Could not prepare this photo — try a different image.');
        });
    }

    // ── Multi-file mode: stage each file via /api/batch-upload ──
    function stageFiles(files) {
        clearScanState();
        var p = buildPanel();
        p.innerHTML = '<div style="padding:30px;text-align:center;color:#64748b;font-size:13px;display:flex;align-items:center;justify-content:center;gap:8px;" data-role="stage-status">' + SPINNER + '<span>Uploading 0/' + files.length + '…</span></div>';
        lockUI(true);
        window.gradingInProgress = true;
        wakeLock.acquire();
        try { p.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
        var st = p.querySelector('[data-role=stage-status]');
        var done = 0;
        var staged = new Array(files.length);
        var jobs = Array.prototype.map.call(files, function (f, i) {
            return prepScanFile(f).then(function (pf) {
            var fd = new FormData();
            fd.append('csrf_token', csrf);
            fd.append('image', pf);
            return fetch('/api/batch-upload', { method: 'POST', body: fd, credentials: 'same-origin' })
                .then(function (r) { return r.json().then(function (j) { return { status: r.status, j: j }; }); })
                .then(function (res) {
                    done++;
                    if (st) st.innerHTML = SPINNER + '<span>Uploading ' + done + '/' + files.length + '…</span>';
                    var j = res.j;
                    if (j && j.success && j.url) {
                        staged[i] = { idx: i, name: f.name, sub_url: j.url, thumb_url: j.thumb_url || j.url, type: 'Card ' + (i + 1), prob: null };
                    } else {
                        staged[i] = { idx: i, name: f.name, failed: true, error: (j && j.error) || 'Upload failed (' + res.status + ')' };
                    }
                })
                .catch(function () { done++; staged[i] = { idx: i, name: f.name, failed: true, error: 'Network error' }; });
            }).catch(function (e) { done++; staged[i] = { idx: i, name: f.name, failed: true, error: (e && e.userMsg) || 'Could not prepare this photo' }; });
        });
        Promise.all(jobs).then(function () {
            wakeLock.release();
            lockUI(false);
            window.gradingInProgress = false;
            staged = staged.filter(Boolean);
            var ok = staged.filter(function (c) { return !c.failed; });
            var bad = staged.filter(function (c) { return c.failed; });
            if (!ok.length) {
                var lines = bad.slice(0, 5).map(function (c) {
                    return (c.name || ('File ' + (c.idx + 1))) + (c.error ? ' — ' + c.error : '');
                });
                failScan('All ' + staged.length + ' upload' + (staged.length > 1 ? 's' : '') + ' failed — check the images and try again.\n\n' + lines.join('\n'));
                return;
            }
            ok.forEach(function (c, i) { c.idx = i; });
            fileUrls = ok.map(function (c) { return c.sub_url; });
            scanId = null;
            scanCards = ok;
            pricePer = window.GRADE_CREDITS || null;
            renderPanel(ok);
            if (bad.length) {
                notice(bad.length + ' file' + (bad.length > 1 ? 's' : '') + ' failed to upload and ' + (bad.length > 1 ? 'were' : 'was') + ' skipped: ' + bad.map(function (c) { return c.name || ('file ' + (c.idx + 1)); }).join(', '));
            }
        });
    }

    // ── Multi-scan trigger: dedicated picker next to the upload zones ──
    var trigger = document.createElement('div');
    trigger.style.cssText = 'margin-top:10px;text-align:center;';
    trigger.innerHTML =
        '<input type="file" id="multi-scan-input" accept="image/*"' + (isBatchPage ? ' multiple' : '') + ' style="display:none;">' +
        '<button type="button" id="multi-scan-btn" class="btn btn-secondary" style="font-size:12px;padding:8px 14px;border-radius:10px;display:inline-flex;align-items:center;gap:6px;">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>' +
        (isBatchPage ? 'Select up to 10 card photos — or one multi-card photo' : 'Multiple cards in one photo? Scan them all →') + '</button>';
    trigger.hidden = !isBatchPage;
    var btnWrap = form.querySelector('.scan-button-wrapper');
    (btnWrap ? btnWrap.parentNode : form).insertBefore(trigger, btnWrap ? btnWrap.nextSibling : null);
    var inp = trigger.querySelector('#multi-scan-input');
    trigger.querySelector('#multi-scan-btn').addEventListener('click', function () { inp.click(); });
    inp.addEventListener('change', function () {
        var files = inp.files ? Array.prototype.slice.call(inp.files, 0, 10) : [];
        if (files.length > 1 && isBatchPage) stageFiles(files);
        else if (files.length) runScan(files[0]);
        inp.value = '';
    });

    // ── Resume hydration ──
    function hydrateScan(sid, opts) {
        fetch('/api/detect-multi?scan_id=' + encodeURIComponent(sid), { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (j) {
                if (!j || !j.success || !j.cards || !j.cards.length) {
                    failScan('Saved scan expired — rescan the image.', true);
                    return;
                }
                scanId = j.scan_id;
                scanCards = j.cards;
                pricePer = (j.price && j.price.grade) || window.GRADE_CREDITS || null;
                renderPanel(j.cards, opts || {});
            })
            .catch(function () { failScan('Could not load the saved scan — rescan the image.', true); });
    }

    var qs = new URLSearchParams(location.search);
    var resume = qs.get('resume');
    if (resume) {
        fetch('/api/pending-action?token=' + encodeURIComponent(resume), { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (j) {
                if (!j || !j.success || j.tool !== 'grade_batch' || !j.payload) return;
                resumeToken = resume;
                var sel = j.payload.selected || [];
                if (j.payload.scan_id) {
                    hydrateScan(j.payload.scan_id, { selected: sel, restored: true });
                } else if (j.payload.image_urls && j.payload.image_urls.length) {
                    fileUrls = j.payload.image_urls;
                    var cards = fileUrls.map(function (u, i) {
                        return { idx: i, sub_url: u, thumb_url: u.replace(/\.[a-z0-9]+$/i, '') + '_th.webp', type: 'Card ' + (i + 1) };
                    });
                    scanCards = cards;
                    pricePer = window.GRADE_CREDITS || null;
                    renderPanel(cards, { selected: sel, restored: true });
                }
            })
            .catch(function () {});
    } else if (qs.get('scan')) {
        hydrateScan(qs.get('scan'));
    }
})();
